import React from "react";
import {
  Box,
  Typography,
  Skeleton,
  useTheme,
} from "@mui/material";
import {
  CalendarMonth,
  ChevronRight,
  FmdGoodOutlined,
  MeetingRoomOutlined,
  FavoriteBorderOutlined,
  WorkOutlineOutlined,
} from "@mui/icons-material";
import { ILead, IMultipleEvent } from "../../../../redux/leads/leads.types";

interface IEventSelectionStepProps {
  events: IMultipleEvent[];
  guestName: string;
  activeLeadUuid?: string;
  selectedLeadData?: ILead;
  loading?: boolean;
  onSelectEvent: (leadUuid: string) => void;
}

const EVENT_ICONS = [
  <FavoriteBorderOutlined sx={{ fontSize: 22 }} />,
  <WorkOutlineOutlined sx={{ fontSize: 22 }} />,
  <CalendarMonth sx={{ fontSize: 22 }} />,
];

function fmtDate(date?: string | null): string {
  if (!date) return "—";
  try {
    return new Date(date).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return date;
  }
}

export const EventSelectionStep: React.FC<IEventSelectionStepProps> = ({
  events,
  guestName,
  activeLeadUuid,
  selectedLeadData,
  loading,
  onSelectEvent,
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Box sx={{ px: 2.5, pt: 2, pb: 5 }}>
        {/* Skeleton for guest details strip */}
        <Skeleton variant="rounded" height={56} sx={{ borderRadius: 2, mb: 3 }} />

        {/* Skeleton for heading */}
        <Skeleton variant="text" width={220} height={40} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width={300} height={20} sx={{ mb: 2.5 }} />

        {/* Skeleton for event cards */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.75 }}>
          {[1, 2, 3, 4].map((i) => (
            <Box
              key={i}
              sx={{
                flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 8px)", md: "1 1 calc(33.33% - 12px)", lg: "1 1 calc(25% - 14px)" },
                maxWidth: { xs: "100%", sm: "calc(50% - 8px)", md: "calc(33.33% - 12px)", lg: "calc(25% - 14px)" },
              }}
            >
              <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3.5 }} />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2.5, pt: 2, pb: 5 }}>
      {/* Guest Details — horizontal strip */}
      {selectedLeadData && (
        <Box
          sx={{
            bgcolor: "#FFFFFF",
            borderRadius: 2,
            px: 2.5,
            py: 1.5,
            mb: 3,
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
            border: "1px solid #E2E8F0",
            display: "flex",
            alignItems: "center",
            gap: 3,
          }}
        >
          {/* Icon */}
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              bgcolor: "#e6f4ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              color: "#1a97f5",
            }}
          >
            <WorkOutlineOutlined sx={{ fontSize: 18 }} />
          </Box>

          <GuestCell label="GUEST NAME" value={selectedLeadData.name_of_guest} />
          <GuestCell
            label="CONTACT"
            value={`${selectedLeadData.country_code ?? ""} ${selectedLeadData.contact_number_one}`}
          />
          <GuestCell label="ENQUIRY DATE" value={fmtDate(selectedLeadData.date_of_visit)} />
          <GuestCell label="HANDLED BY" value={selectedLeadData.handled_by_employee} />
        </Box>
      )}

      {/* Heading */}
      <Typography sx={{ fontSize: "2rem", fontWeight: 700, lineHeight: 1.15, letterSpacing: -0.5, mb: 0.75 }}>
        Choose your event
      </Typography>
      <Typography sx={{ fontSize: "0.9rem", color: "text.secondary", mb: 2.5, lineHeight: 1.4 }}>
        Select an event for {guestName} to build its food menu.
      </Typography>

      {/* Event cards */}
      <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 1.75 }}>
        {events.map((event, idx) => {
          const eventLabel = event.event_type || "Event";
          const isDark = false;
          const Icon = EVENT_ICONS[idx % EVENT_ICONS.length];

          return (
            <Box
              key={event.leads_uuid}
              onClick={() => onSelectEvent(event.leads_uuid as string)}
              sx={{
                bgcolor: isDark ? "#1E293B" : "#FFFFFF",
                borderRadius: 3.5,
                overflow: "hidden",
                cursor: "pointer",
                WebkitTapHighlightColor: "transparent",
                boxShadow: isDark
                  ? "0 6px 24px rgba(0,0,0,0.22)"
                  : "0 1px 6px rgba(0,0,0,0.06)",
                border: isDark ? "none" : "1px solid #E2E8F0",
                flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 8px)", md: "1 1 calc(33.33% - 12px)", lg: "1 1 calc(25% - 14px)" },
                maxWidth: { xs: "100%", sm: "calc(50% - 8px)", md: "calc(33.33% - 12px)", lg: "calc(25% - 14px)" },
                "&:active": { transform: "scale(0.984)" },
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: isDark
                    ? "0 12px 32px rgba(0,0,0,0.3)"
                    : "0 4px 16px rgba(0,0,0,0.1)",
                },
              }}
            >
              {/* Top Section */}
              <Box sx={{ px: 2, pt: 2, pb: 1.5, display: "flex", alignItems: "center", gap: 1.75 }}>
                {/* Icon */}
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: "50%",
                    bgcolor: isDark ? "rgba(255,255,255,0.08)" : "#e6f4ff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    color: isDark ? "#FFFFFF" : "#1a97f5",
                  }}
                >
                  {Icon}
                </Box>

                {/* Event Info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      sx={{
                        fontSize: "1.1rem",
                        fontWeight: 700,
                        color: isDark ? "#FFFFFF" : "#1E293B",
                        lineHeight: 1.2,
                      }}
                      noWrap
                    >
                      {eventLabel}
                    </Typography>
                    {event.actual_status && (
                      <Box
                        sx={{
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          fontSize: "0.6rem",
                          fontWeight: 700,
                          letterSpacing: "0.05em",
                          bgcolor: isDark ? "rgba(26,151,245,0.3)" : "#e6f4ff",
                          color: isDark ? "#93C5FD" : "#1a97f5",
                        }}
                      >
                        {event.actual_status.toUpperCase()}
                      </Box>
                    )}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "0.82rem",
                      color: isDark ? "rgba(255,255,255,0.7)" : "#64748B",
                    }}
                    noWrap
                  >
                   {event.function_date ? fmtDate(event.function_date) : ""}{event.number_of_guests != null ? ` · ${event.number_of_guests} guests` : ""}
                  </Typography>
                </Box>

                {/* Arrow */}
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    bgcolor: isDark ? "#1a97f5" : "#e6f4ff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <ChevronRight sx={{ fontSize: 22, color: isDark ? "#FFFFFF" : "#64748B" }} />
                </Box>
              </Box>

              {/* Divider */}
              <Box
                sx={{
                  mx: 2,
                  borderTop: "1px solid",
                  borderColor: isDark ? "rgba(255,255,255,0.08)" : "#E2E8F0",
                }}
              />

              {/* Bottom Section — Details grid */}
              <Box sx={{ px: 2, py: 1.5, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 0.5 }}>
                <EventCell
                  icon={<CalendarMonth sx={{ fontSize: 11 }} />}
                  label="FOLLOWUP"
                  value={event.followup_date ? fmtDate(event.followup_date) : "—"}
                  dark={isDark}
                />
                <EventCell
                  icon={<FmdGoodOutlined sx={{ fontSize: 11 }} />}
                  label="VENUE"
                  value={event.preferred_venue || "—"}
                  dark={isDark}
                />
                <EventCell
                  icon={<MeetingRoomOutlined sx={{ fontSize: 11 }} />}
                  label="ROOMS"
                  value={event.number_of_rooms ?? "—"}
                  dark={isDark}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const GuestCell: React.FC<{ label: string; value?: string | number | null }> = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      sx={{
        fontSize: "0.6rem",
        fontWeight: 700,
        color: "#94A3B8",
        letterSpacing: 0.8,
        textTransform: "uppercase",
        lineHeight: 1,
        mb: 0.4,
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#1E293B", lineHeight: 1.3 }} noWrap>
      {value ?? "—"}
    </Typography>
  </Box>
);

const EventCell: React.FC<{
  icon: React.ReactNode;
  label: string;
  value?: string | number | null;
  dark?: boolean;
}> = ({ icon, label, value, dark }) => (
  <Box sx={{ minWidth: 0 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.35, mb: 0.4 }}>
      <Box sx={{ color: dark ? "rgba(255,255,255,0.5)" : "#94A3B8", lineHeight: 1 }}>{icon}</Box>
      <Typography
        sx={{
          fontSize: "0.6rem",
          fontWeight: 700,
          letterSpacing: 0.8,
          textTransform: "uppercase",
          color: dark ? "rgba(255,255,255,0.5)" : "#94A3B8",
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>
    </Box>
    <Typography
      sx={{
        fontSize: "0.875rem",
        fontWeight: 700,
        color: dark ? "#FFFFFF" : "#1E293B",
        lineHeight: 1.3,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {value ?? "—"}
    </Typography>
  </Box>
);
